import { reach, ObjectSchema } from 'yup';
import { getFormData } from '@bit/necta.hooks.get-form-data';
import { mapArrayToObject } from './data-maps';
import { generatePassword } from './generate-password';
import { PASSWORD_ERROR } from './constants';
import { get } from 'lodash';
import Moment from 'moment';
import { DatePicker, Input, Password, Select, Tags, CheckBox } from '../components';


export type Field = {
  component?: React.FC;
  label: string;
  meta?: any;
  name: string;
  required?: boolean;
  // parent?: string;
  value?: any;
  typeInfo?: TypeInfo;
  fields?: Field[];
};

export type TypeInfo = {
  type: string;
  description: string;
};

const DEFAULT_STRING_INFO: TypeInfo = { type: 'string', description: '' };
const DEFAULT_BOOLEAN_INFO: TypeInfo = {
  type: 'boolean',
  description: 'true or false',
};
const DEFAULT_DATE_INFO: TypeInfo = {
  type: 'date',
  description: 'Format: YYYY/MM/DD. Eg: 1992/04/19',
};
const DEFAULT_PASSWORD_INFO: TypeInfo = {
  type: 'password',
  description: PASSWORD_ERROR + '. Leave blank to have an auto-generated password.',
};
const DEFAULT_SELECT_INFO: TypeInfo = { type: 'select', description: '' };
const DEFAULT_MULTISELECT_INFO: TypeInfo = { type: 'multiselect', description: 'A selection list separated by a semicolon (;) Eg: One;Two;Three' };
const DEFAULT_ARRAY_INFO: TypeInfo = { type: 'array', description: 'A list separated by a semicolon (;) Eg: One;Two;Three' };

/**
 * Map fields based on schema
 * @param {ObjectSchema}            objectSchema     The yup schema to base the field types on.
 * @param {string}                  parent           (Optional) The parent of the current schema object. Used for nested schemas.
 */
export const mapFieldsFromSchema = (objectSchema: ObjectSchema<any>, parent?: string): Field[] => {
  const { schema, fields } = getFormData(objectSchema);
  const fieldsArr: Field[] = [];

  Object.keys(fields).forEach((v: any) => {
    const field = reach(schema, v) as any;
    const name = parent ? parent + '.' + v : v;

    if (field.meta()?.exclude) return

    if (field.fields) {
      const fields = mapFieldsFromSchema(field, name);
      const fieldData = { ...fields[v], fields, name };
      fieldsArr.push(fieldData);
      return;
    }

    const fieldData = {
      ...fields[v],
      component: Input,
      type: field._type,
      name,
    };
    switch (field._type) {
      case 'string':
        fieldData.component = Input;
        fieldData.typeInfo = DEFAULT_STRING_INFO;
        break;
      case 'boolean':
        fieldData.component = CheckBox;
        fieldData.typeInfo = DEFAULT_BOOLEAN_INFO;
        break;
      case 'date':
        fieldData.component = DatePicker;
        fieldData.typeInfo = DEFAULT_DATE_INFO;
        break;
      case 'array':
        fieldData.component = Tags;
        fieldData.typeInfo = DEFAULT_ARRAY_INFO;
        break;
      default:
        fieldData.component = Input;
        fieldData.typeInfo = DEFAULT_STRING_INFO;
        break;
    }
    if (field.meta()) {
      switch (field.meta().type) {
        case 'password':
          fieldData.component = Password;
          fieldData.typeInfo = DEFAULT_PASSWORD_INFO;
          break;
        case 'select':
          const whitelist: string[] = Array.from(field._whitelist.list);
          fieldData.options = mapArrayToObject(whitelist);
          fieldData.component = Select;
          fieldData.allowClear = true;
          if (whitelist && whitelist.length > 0) {
            fieldData.typeInfo = {
              type: 'select',
              description: 'One of: ' + makeString(whitelist),
            };
          } else {
            fieldData.typeInfo = DEFAULT_SELECT_INFO;
          }
          break;
        case 'multiselect':
          const _whitelist: string[] = Array.from(field.meta().options);
          fieldData.options = mapArrayToObject(_whitelist);
          fieldData.component = Select;
          fieldData.mode = 'multiple';
          fieldData.allowClear = true;
          if (_whitelist && _whitelist.length > 0) {
            fieldData.typeInfo = {
              type: 'multiselect',
              description: 'Many of: ' + makeString(_whitelist) + '. Separated by a semicolon(;)',
            };
          } else {
            fieldData.typeInfo = DEFAULT_MULTISELECT_INFO;
          }
          fieldData.multiple = true;
          fieldData.type = 'array';
          break;
        case 'date':
          fieldData.component = DatePicker;
          fieldData.typeInfo = DEFAULT_DATE_INFO;
          break;
      }
    }
    fieldsArr.push(fieldData);
  });

  return fieldsArr;
};

/**
 * Map object into a nested object based on the field array.
 * @param {any}                 data            An object containing data
 * @param {Field[]}             fields          An array of Fields (created from a schema with mapFieldsFromSchema)
 */
export const mapDataIntoSchema = (data: any, fields: Field[]): any => {
  const fieldsObj: any = fields.reduce((acc: any, f: any) => {
    const name = f.name.split('.').reverse()[0];

    if (f.fields) {
      return { ...acc, [name]: mapDataIntoSchema(data, f.fields) };
    }

    const parsed = parse(data[f.label], f);
    return { ...acc, [name]: parsed };
  }, {});

  return fieldsObj;
};

/**
 * Parse a value based on a Field containing parsing info
 * @param {any}                 value             Any value
 * @param {Field}               field             A Field created from a schema with mapFieldsFromSchema
 */
const parse = (value: any, field: Field): any => {
  const type = get(field, 'typeInfo.type');
  switch (type) {
    case 'date':
      return Moment(value, 'YYYY/MM/DD').toISOString();
    case 'boolean':
      return !!value ? JSON.parse(value.toLowerCase()) : false;
    case 'password':
      return value || generatePassword();
    case 'multiselect':
      return value ? value.split(';') : [];
    case 'array':
      return value ? value.split(';') : [];
    case 'string':
      return value || null;
    default:
      return value || null;
  }
};

// TODO: maybe move to more generic helper
// Turns an array of strings containing options into human readable text.
// EG: ['one', 'two', 'three'] => one, two or three
const makeString = (arr: string[]): string => {
  const _arr = arr.filter(n => n);
  if (_arr.length === 1) return _arr[0];
  const firsts = _arr.slice(0, _arr.length - 1);
  const last = _arr[_arr.length - 1];
  return firsts.join(', ') + ' or ' + last;
};
